import { useState } from "react";
import { Reply, ShieldCheck, Undo2, XOctagon } from "lucide-react";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { TransactionDto } from "@/features/wallet/api/dto";
import { useConfirmLedgerByReference } from "@/features/wallet/api/use-confirm-ledger-by-reference";
import { useMarkFailedByReference } from "@/features/wallet/api/use-mark-failed-by-reference";
import { useReverseByReference } from "@/features/wallet/api/use-reverse-by-reference";
import { useCancelTransaction } from "@/features/wallet/api/use-cancel-transaction";

type WorkerKind = "confirm" | "markFailed" | "reverse" | "cancel";

/**
 * Actions wallet par **référence fonctionnelle** (guide v2.5 §6.3) + cancel
 * Non-Member (v2.4). Voies principales des workers ledger-service /
 * kyc-service — toutes exposées côté back-office pour reprise manuelle.
 *
 * - `confirm-ledger-by-reference` : acquitte la comptabilisation
 * - `mark-failed-by-reference`   : répercute un rejet comptable
 * - `reverse-by-reference`       : répercute une contre-passation
 * - `cancel`                     : annule un transfert Non-Member non encaissé
 *
 * Rôles : `flashpay-service`, `flashpay-superadmin`, `flashpay-admin`.
 */
export function TransactionWorkerByReferenceActions({
  transaction,
}: {
  transaction: TransactionDto;
}) {
  const { t } = useTypedTranslation();
  const tx = (key: string) => t(key as never, {} as never) as string;

  const confirmLedger = useConfirmLedgerByReference();
  const markFailed = useMarkFailedByReference();
  const reverse = useReverseByReference();
  const cancel = useCancelTransaction();

  const [openKind, setOpenKind] = useState<WorkerKind | null>(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>();

  const pending =
    confirmLedger.isPending ||
    markFailed.isPending ||
    reverse.isPending ||
    cancel.isPending;

  const closeAll = () => {
    setOpenKind(null);
    setReason("");
    setReasonError(undefined);
  };

  const handleConfirm = () => {
    if (!openKind) return;
    if (openKind === "reverse" && !reason.trim()) {
      setReasonError(tx("requiredFieldError"));
      return;
    }
    if (openKind === "cancel") {
      cancel.mutate(transaction.id, {
        onSuccess: () => {
          notifSilence(tx("wallet.workers.feedback.cancelled"));
          closeAll();
        },
        onError: (error) => notifSilenceError(apiErrorHandler(error)),
      });
      return;
    }
    const body = {
      reference: transaction.reference,
      reason: reason.trim() || undefined,
    };
    if (openKind === "confirm") {
      confirmLedger.mutate(body, {
        onSuccess: () => {
          notifSilence(tx("wallet.workers.feedback.ledgerConfirmed"));
          closeAll();
        },
        onError: (error) => notifSilenceError(apiErrorHandler(error)),
      });
      return;
    }
    if (openKind === "markFailed") {
      markFailed.mutate(body, {
        onSuccess: () => {
          notifSilence(tx("wallet.workers.feedback.markedFailed"));
          closeAll();
        },
        onError: (error) => notifSilenceError(apiErrorHandler(error)),
      });
      return;
    }
    if (openKind === "reverse") {
      reverse.mutate(
        { reference: transaction.reference, reason: reason.trim() },
        {
          onSuccess: () => {
            notifSilence(tx("wallet.workers.feedback.reversed"));
            closeAll();
          },
          onError: (error) => notifSilenceError(apiErrorHandler(error)),
        }
      );
    }
  };

  const dialogTitle = openKind
    ? tx(`wallet.workers.${openKind}.title`)
    : "";
  const dialogDescription = openKind
    ? tx(`wallet.workers.${openKind}.description`)
    : "";
  const dialogConfirm = openKind
    ? tx(`wallet.workers.${openKind}.action`)
    : "";

  // reverse accepté sur COMPLETED (contre-passation ledger directe) et sur
  // DISPUTED (restitution des fonds consommant le gel de la contestation).
  const canCancel =
    transaction.status === "PENDING" || transaction.status === "AWAITING_LEDGER";
  const canConfirmOrFail =
    transaction.status === "AWAITING_LEDGER" || transaction.status === "PENDING";
  const canReverse =
    transaction.status === "COMPLETED" || transaction.status === "DISPUTED";

  if (!canCancel && !canConfirmOrFail && !canReverse) return null;

  return (
    <RequireRole
      roles={[
        FLASHPAY_ROLES.SERVICE,
        FLASHPAY_ROLES.SUPERADMIN,
        FLASHPAY_ROLES.ADMIN,
      ]}
    >
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {tx("wallet.workers.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {tx("wallet.workers.description")}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {canConfirmOrFail ? (
              <Button
                variant="outline"
                onClick={() => setOpenKind("confirm")}
                disabled={pending}
              >
                <ShieldCheck className="mr-2 h-4 w-4" />
                {tx("wallet.workers.confirm.action")}
              </Button>
            ) : null}
            {canConfirmOrFail ? (
              <Button
                variant="destructive"
                onClick={() => setOpenKind("markFailed")}
                disabled={pending}
              >
                <XOctagon className="mr-2 h-4 w-4" />
                {tx("wallet.workers.markFailed.action")}
              </Button>
            ) : null}
            {canReverse ? (
              <Button
                variant="destructive"
                onClick={() => setOpenKind("reverse")}
                disabled={pending}
              >
                <Undo2 className="mr-2 h-4 w-4" />
                {tx("wallet.workers.reverse.action")}
              </Button>
            ) : null}
            {canCancel ? (
              <Button
                variant="ghost"
                onClick={() => setOpenKind("cancel")}
                disabled={pending}
              >
                <Reply className="mr-2 h-4 w-4" />
                {tx("wallet.workers.cancel.action")}
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {openKind ? (
        <ShareablePreview
          data={{
            reference: transaction.reference,
            status: transaction.status,
            amountMinor: transaction.amountMinor,
            currency: transaction.currency,
          }}
          title={dialogTitle}
          description={dialogDescription}
          mode="user"
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !pending) closeAll();
          }}
          maxHeight="90vh"
          customView={() =>
            openKind === "cancel" ? null : (
              <div className="space-y-1.5">
                <Label htmlFor="worker-reason">
                  {openKind === "reverse"
                    ? tx("wallet.workers.reason.required")
                    : tx("wallet.workers.reason.optional")}
                </Label>
                <Textarea
                  id="worker-reason"
                  placeholder={tx("wallet.workers.reason.placeholder")}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (reasonError) setReasonError(undefined);
                  }}
                />
                {reasonError ? (
                  <p className="text-sm text-destructive">{reasonError}</p>
                ) : null}
              </div>
            )
          }
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: closeAll,
            t: (k: string) => String(t(k as never, {} as never)),
            labels: { confirm: dialogConfirm },
            confirmLoading: pending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </RequireRole>
  );
}
